import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AxiosError } from 'axios'

import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUpdateReturnLoss from '@/modules/promotion/api/hooks/useMutateUpdateReturnLoss'
import { UpdateReturnLossRequest } from '@/modules/promotion/type'
import useQueryGetReturnLossById from '@/modules/promotion/api/hooks/useQueryGetReturnLossById'
import Spinner from '@/common/components/Spinner'
import ReturnLossForm from '../../../ReturnLossForm'
import { ReturnLossFormValues } from '../../../ReturnLossForm/components/FormSchema'
import { useQueryClient } from '@tanstack/react-query'
import { ReturnLossQueryKeys } from '@/modules/promotion/keys'
import { ProviderResolutionConflictDialog } from '../../../ProviderResolutionConflictDialog'

export default function EditReturnLossForm() {
	const navigate = useNavigate()
	const { id } = useParams()
	const { data: returnLoss, isLoading } = useQueryGetReturnLossById(id!)
	const queryClient = useQueryClient()
	const [conflictDialogOpen, setConflictDialogOpen] = useState(false)
	const [conflictMessage, setConflictMessage] = useState('')

	const { mutate: updateReturnLoss } = useMutationUpdateReturnLoss({
		onSuccess: () => {
			toast.success('แก้ไขรายการคืนยอดเสียสำเร็จ')
			navigate('/operator/return-loss')
			queryClient.invalidateQueries({
				queryKey: [ReturnLossQueryKeys.GetReturnLossById, id],
			})
		},
		onError: (error) => {
			const axiosError = error as unknown as AxiosError<{
				message: string
				errorCode: string
			}>

			// Check if it's a provider resolution conflict error
			if (
				axiosError.isAxiosError &&
				axiosError.response?.data?.errorCode === 'PROVIDER_RESOLUTION_CONFLICT'
			) {
				setConflictMessage(axiosError.response.data.message)
				setConflictDialogOpen(true)
				return
			}

			// For other errors, show toast
			showErrorToast(error)
		},
	})

	const handleSubmit = (payload: UpdateReturnLossRequest) => {
		updateReturnLoss({
			id: id!,
			payload,
		})
	}

	if (isLoading || !returnLoss) return <Spinner />

	return (
		<>
			<ReturnLossForm
				mode="edit"
				defaultValues={returnLoss as unknown as Partial<ReturnLossFormValues>}
				onSubmit={handleSubmit}
			/>
			<ProviderResolutionConflictDialog
				isOpen={conflictDialogOpen}
				onClose={() => setConflictDialogOpen(false)}
				message={conflictMessage}
			/>
		</>
	)
}
